// src/modules/units/tyres/views/dashboard/tyredashboard.js
//
// The tyre-management landing page, in three bands:
//
//   1. Status tiles — how many tyres are where. Now buttons: each opens the
//      register filtered to that status, so a count is a way in rather than a
//      number you then have to go and find.
//   2. Lifecycle actions — six large tiles in the order a tyre travels.
//   3. Lookup — a quieter row for find / register / reports.
//
// This replaced a radial ring of eight equal circles. The ring needed JS to
// decide between ring and grid at a breakpoint; a plain CSS grid reflows on its
// own, so RING_MIN_WIDTH, FORCE_GRID, applyLayout and the matchMedia listener
// (plus its teardown) are all gone with it. Nothing here is hand-positioned —
// both grids come from the arrays in tyre-actions.js.

import { fetchAllTyres } from '../tyres.api.js';
import { BIN_ORDER, BIN_META, statusBin } from '../../shared/tyre-status.js';
import { TYRE_LIFECYCLE_ACTIONS, TYRE_RECORD_ACTIONS, TYRE_SETUP_ACTIONS } from '../../shared/tyre-actions.js';
import { icon } from '../../shared/tyre-icons.js';
import { formatCount } from '../../shared/tyre-format.js';
import { onTyreEvent, TYRE_EVENTS } from '../../shared/tyre-events.js';
import { getSelectedYardId } from '../../shared/tyre-scope.js';
import { canArchiveTyres } from '../../shared/tyre-access.js';

// The tiles ARE the bins — see TYRE_BIN in tyre-status.js. Ordered the way a tyre
// travels, matching the action grid below, and ending with the two exceptions.
//
// Under Repair and Inspection are gone as tiles on purpose. Repair and inspection
// are things you DO to a tyre, recorded in the action log; they are not places a
// tyre lives. A repaired tyre is Away until it comes back, then it is In Stock or
// Fitted like anything else — and an inspection never moves it at all.
const TILES = BIN_ORDER;

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

function tileMarkup(bin) {
  const meta = BIN_META[bin];
  return `
    <button type="button" class="tm-tile" data-bin="${bin}">
      <span class="tm-tile-badge tm-tone-${meta.tone}">${icon('tyre')}</span>
      <span class="tm-tile-body">
        <span class="tm-tile-count is-loading" data-count>—</span>
        <span class="tm-tile-label">${meta.label}</span>
      </span>
    </button>`;
}

function actionMarkup(action) {
  const tone = action.variant === 'outline' ? 'tm-act-outline' : `tm-tone-${action.tone}`;
  return `
    <button type="button" class="tm-act ${tone}" data-action="${action.id}">
      <span class="tm-act-ic">${icon(action.icon)}</span>
      <span class="tm-act-verb">${action.label}</span>
      <span class="tm-act-sub">${action.sub}</span>
    </button>`;
}

function lookupMarkup(action) {
  return `
    <button type="button" class="tm-link-btn" data-action="${action.id}">
      <span class="tm-link-ic">${icon(action.icon)}</span>${action.label}
    </button>`;
}

async function loadCounts(rootEl) {
  // BRIDGE — the fleet is fetched once and bucketed here, rather than asking the
  // server for six counts.
  //
  // It has to be. A bin is DERIVED (statusBin), and ?tyreStatus= filters on the
  // STORED value, so the two disagree the moment the backend stamps a status the
  // association contradicts. A tyre stamped UNDER_REPAIR would be absent from
  // Fitted, In Service AND In Stock — and with no Under Repair tile left to catch
  // it, absent from the dashboard entirely. Six cheap wrong numbers are worse
  // than one honest fetch.
  //
  // This stays correct whatever the backend does, which is what makes it a bridge
  // and not a hack. When the backend can answer by bin — a /api/tyre/summary
  // returning these five numbers — replace the body of this function and nothing
  // around it changes.
  const setAll = (fn) => TILES.forEach((bin) => {
    const elc = rootEl.querySelector(`.tm-tile[data-bin="${bin}"] [data-count]`);
    if (!elc) return;
    elc.textContent = fn(bin);
    elc.classList.remove('is-loading');
  });

  try {
    // Scope to the selected yard; getSelectedYardId() is null for All yards, and
    // the query builder drops a null yardId, so the sweep is the whole customer.
    const res = await fetchAllTyres({ yardId: getSelectedYardId() });
    if (!res.success) { setAll(() => '—'); return; }

    const rows = res.data?.results || [];
    const tally = Object.create(null);
    // Tyres that have left the fleet return a null bin and are counted nowhere.
    rows.forEach((t) => {
      const bin = statusBin(t);
      if (bin) tally[bin] = (tally[bin] || 0) + 1;
    });

    setAll((bin) => formatCount(tally[bin] || 0));
  } catch {
    setAll(() => '—');
  }
}

/**
 * Render the dashboard into `viewEl`.
 * @param {HTMLElement} viewEl the controller's view slot (emptied first)
 * @param {object} ctx
 * @param {(id:string)=>void} [ctx.onAction] an action or lookup tile was pressed
 * @param {(bin:string)=>void} [ctx.onBin] a bin tile was pressed
 * @returns {() => void} teardown that unsubscribes this view's listeners
 */
export function renderTyreDashboard(viewEl, ctx = {}) {
  // Administrator-only destinations (Archived tyres) are left out entirely for
  // everyone else rather than shown disabled: a standard user has no use for them.
  const isAdmin = canArchiveTyres();
  const visible = (a) => !a.adminOnly || isAdmin;

  const dash = el(`
    <div class="tm-dashboard">
      <div class="tm-tiles">
        ${TILES.map(tileMarkup).join('')}
      </div>

      <div class="tm-acts">
        ${TYRE_LIFECYCLE_ACTIONS.map(actionMarkup).join('')}
      </div>

      <div class="tm-dashboard-lower">
        <section class="tm-dashboard-group" aria-labelledby="tm-records-heading">
          <h2 id="tm-records-heading" class="tm-dashboard-group-title">Records</h2>
          <div class="tm-links tm-links-records">
            ${TYRE_RECORD_ACTIONS.filter(visible).map(lookupMarkup).join('')}
          </div>
        </section>

        <section class="tm-dashboard-group" aria-labelledby="tm-setup-heading">
          <h2 id="tm-setup-heading" class="tm-dashboard-group-title">Setup</h2>
          <div class="tm-links tm-links-setup">
            ${TYRE_SETUP_ACTIONS.filter(visible).map(lookupMarkup).join('')}
          </div>
        </section>
      </div>
    </div>
  `);

  // One delegated listener for the whole page: the three grids are all buttons
  // carrying either data-action or data-status, so nothing needs re-binding when
  // a grid is re-rendered.
  dash.addEventListener('click', (e) => {
    const act = e.target.closest('[data-action]');
    if (act) {
      if (typeof ctx.onAction === 'function') ctx.onAction(act.dataset.action);
      return;
    }
    const tile = e.target.closest('[data-bin]');
    if (tile && typeof ctx.onBin === 'function') ctx.onBin(tile.dataset.bin);
  });

  viewEl.replaceChildren(dash);

  // Initial fill, then refresh whenever a mutation elsewhere reports a change,
  // or the yard scope changes — both move the set of tyres these counts describe.
  loadCounts(dash);
  const offChanged = onTyreEvent(TYRE_EVENTS.CHANGED, () => loadCounts(dash));
  const offScope = onTyreEvent(TYRE_EVENTS.SCOPE_CHANGED, () => loadCounts(dash));

  return () => { offChanged(); offScope(); };
}
